import { TooltipProvider } from '@sho-bot/ui';
import { Suspense, useEffect, type ReactNode } from 'react';
import { useGuilds } from './api/queries.js';
import { AppShell } from './components/app-shell.js';
import { SessionSkeleton, ViewSkeleton } from './components/skeletons.js';
import { ErrorState, PartialFailureNotice } from './components/states.js';
import { useLocationState } from './navigation.js';
import { lazyPage } from './lazy-page.js';
import { LoginPage } from './pages/login-page.js';
import { useSession } from './session.js';

const OverviewPage = lazyPage(async () => {
  const page = await import('./pages/overview-page.js');
  return { default: page.OverviewPage };
});
const MemoryPage = lazyPage(async () => {
  const page = await import('./pages/memory-page.js');
  return { default: page.MemoryPage };
});
const QueryLabPage = lazyPage(async () => {
  const page = await import('./pages/query-lab-page.js');
  return { default: page.QueryLabPage };
});
const OperationsPage = lazyPage(async () => {
  const page = await import('./pages/operations-page.js');
  return { default: page.OperationsPage };
});
const ServerSettingsPage = lazyPage(async () => {
  const page = await import('./pages/server-settings-page.js');
  return { default: page.ServerSettingsPage };
});
const ShoBotSettingsPage = lazyPage(async () => {
  const page = await import('./pages/sho-bot-settings-page.js');
  return { default: page.ShoBotSettingsPage };
});

/**
 * The application root.
 *
 * Three states, and only the third renders anything operational: unknown, while
 * the session is being restored; signed out, which is the login form; and signed
 * in, which is the shell.
 *
 * The signed-out branch unmounts the whole shell, which is what makes "clear
 * results on logout" true rather than aspirational — there is no component left
 * holding a Query Lab result.
 */
export function App(): ReactNode {
  const { status, refresh } = useSession();

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (status === 'unknown') {
    return <SessionSkeleton />;
  }
  if (status === 'signed-out') return <LoginPage />;
  return (
    <TooltipProvider>
      <SignedIn />
    </TooltipProvider>
  );
}

function SignedIn(): ReactNode {
  const { signOut } = useSession();
  const { location, navigate, selectGuild } = useLocationState();
  const guilds = useGuilds();

  // A guild in the URL that this deployment does not have is not silently
  // replaced with a different one: the operator asked for a specific server, and
  // quietly showing them another server's numbers is worse than showing none.
  const known = guilds.data?.guilds ?? [];
  const selected =
    location.guildId !== null && known.some((guild) => guild.guildId === location.guildId)
      ? location.guildId
      : null;

  return (
    <AppShell
      currentPath={location.path}
      onNavigate={navigate}
      guilds={known}
      selectedGuildId={selected}
      onSelectGuild={selectGuild}
      onSignOut={() => void signOut()}
    >
      <div className="flex flex-col gap-12">
        {guilds.isError ? (
          <ErrorState error={guilds.error} onRetry={() => void guilds.refetch()} />
        ) : null}

        {guilds.data !== undefined && !guilds.data.botReachable ? (
          <PartialFailureNotice>
            The bot process could not be reached. Server names come from the archive, live cache
            state is unknown, and the operations controls and Query Lab need the bot to be running.
          </PartialFailureNotice>
        ) : null}

        {location.guildId !== null && selected === null && guilds.data !== undefined ? (
          <PartialFailureNotice>
            The server in this URL is not one this deployment knows about. Pick one from the
            sidebar.
          </PartialFailureNotice>
        ) : null}

        <Suspense fallback={<ViewSkeleton path={location.path} />}>
          {location.path === '/' ? <OverviewPage /> : null}
          {location.path === '/memory' ? <MemoryPage guildId={selected} /> : null}
          {location.path === '/query-lab' ? <QueryLabPage guildId={selected} /> : null}
          {location.path === '/operations' ? <OperationsPage guildId={selected} /> : null}
          {location.path === '/server-settings' ? <ServerSettingsPage guildId={selected} /> : null}
          {location.path === '/settings' ? <ShoBotSettingsPage /> : null}
        </Suspense>
      </div>
    </AppShell>
  );
}
